import { useCallback, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useUnmount } from "react-use";
import { useChannelEventListener, useChannelEventTrigger } from "@activecollab/activecollab-react-pusher";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import { getLoggedUserId } from "~/store/selectors/selector.users";
import { usePageVisibility } from "./usePageVisibility.jsx";

export const useWhoIsWriting = (context, registerListeners = false) => {
  if (typeof context !== "string" && context.length === 0) {
    throw new TypeError(`context expected string, received: ${typeof context}`);
  }

  const isVisible = usePageVisibility();

  const [userIds, setUserIds] = useState([]);
  const debouncers = useRef({});

  const trigger = useChannelEventTrigger();
  const loggedUserId = useSelector(getLoggedUserId);

  const stopWriting = () => {
    trigger(`client-ac-stop_writing_${context}`, { userId: loggedUserId });
  };

  const startWriting = throttle(
    () => {
      trigger(`client-ac-start_writing_${context}`, { userId: loggedUserId });
    },
    1000,
    { trailing: false }
  );

  useEffect(() => {
    isVisible
      ? trigger("client-ac-page_focus", { userId: loggedUserId })
      : trigger("client-ac-page_blur", { userId: loggedUserId });
  }, [isVisible, loggedUserId, trigger]);

  useUnmount(() => {
    Object.keys(debouncers.current).forEach((userId) => {
      if (debouncers.current[userId] && typeof debouncers.current[userId]?.cancel === "function")
        debouncers.current[userId].cancel();
    });
  });

  const removeUserId = useCallback((userId) => {
    setUserIds((prevState) => {
      if (prevState.includes(userId)) {
        return prevState.filter((id) => id !== userId);
      }
      return prevState;
    });
  }, []);

  const mapAddingCommentEventProps = useCallback(
    ({ userId }) => {
      if (registerListeners) {
        if (!debouncers.current[userId]) {
          let debouncer = debounce(() => {
            removeUserId(userId);
          }, 3000);

          debouncers.current[userId] = debouncer;
          typeof debouncer === "function" && debouncer();
        } else {
          debouncers.current[userId]();
        }

        setUserIds((prevState) => {
          if (prevState.indexOf(userId) < 0) {
            return [...prevState, userId];
          }
          return prevState;
        });
      }
    },
    [debouncers, registerListeners, removeUserId]
  );

  const mapRemoveUserEventProps = useCallback(
    ({ userId }) => {
      if (registerListeners) {
        if (debouncers.current[userId] && typeof debouncers.current[userId]?.cancel === "function") {
          debouncers.current[userId].cancel();
        }

        removeUserId(userId);
      }
    },
    [registerListeners, removeUserId]
  );

  const mapMemberRemovedEventProps = useCallback(
    ({ id }) => {
      if (registerListeners) {
        return mapRemoveUserEventProps({ userId: Number(id) });
      }
    },
    [mapRemoveUserEventProps, registerListeners]
  );

  useChannelEventListener(`client-ac-start_writing_${context}`, mapAddingCommentEventProps);
  useChannelEventListener(`client-ac-stop_writing_${context}`, mapRemoveUserEventProps);
  useChannelEventListener(`client-ac-page_blur`, mapRemoveUserEventProps);
  useChannelEventListener(`pusher:member_removed`, mapMemberRemovedEventProps);

  return {
    userIds,
    startWriting,
    stopWriting,
  };
};
